import { useEffect, useMemo, useState } from "react";
import { Warehouse, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const NONE = "__none__";

export const HubSettingsSection = () => {
  const [hubs, setHubs] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [orgs, setOrgs] = useState([]);
  const [newHub, setNewHub] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/hubs"), api.get("/filters")])
      .then(([h, f]) => {
        setHubs(h.data.hubs);
        setAssignments(h.data.assignments);
        setOrgs(f.data.organisations || []);
      })
      .catch(() => toast.error("Hub ayarları yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const addHub = async () => {
    const name = newHub.trim();
    if (!name) return;
    try {
      await api.post("/hubs", { name });
      setHubs((h) => (h.includes(name) ? h : [...h, name].sort((a, b) => a.localeCompare(b, "tr"))));
      setNewHub("");
      toast.success(`"${name}" hub'ı eklendi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hub eklenemedi");
    }
  };

  const removeHub = async (name) => {
    try {
      await api.delete("/hubs", { params: { name } });
      setHubs((h) => h.filter((x) => x !== name));
      setAssignments((a) => Object.fromEntries(Object.entries(a).filter(([, v]) => v !== name)));
      toast.success(`"${name}" hub'ı silindi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const setOrgHub = async (org, hub) => {
    const val = hub === NONE ? "" : hub;
    try {
      await api.post("/org-hubs", { organisation: org, hub: val });
      setAssignments((a) => {
        const next = { ...a };
        if (val) next[org] = val;
        else delete next[org];
        return next;
      });
      toast.success(val ? `${org} → ${val}` : `${org} hub eşleşmesi kaldırıldı`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const visibleOrgs = useMemo(() => {
    if (!search) return orgs;
    const s = search.toLocaleLowerCase("tr");
    return orgs.filter((o) => o.toLocaleLowerCase("tr").includes(s));
  }, [orgs, search]);

  const assignedCount = Object.keys(assignments).length;

  if (loading) return null;

  return (
    <div className="border border-stone-200 bg-white" data-testid="hub-settings-section">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 bg-stone-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <Warehouse size={15} className="text-[#1C4E35]" />
          <h3 className="font-heading text-sm font-black uppercase tracking-[0.12em] text-[#0A0D0B]">Hub Ayarları</h3>
          <span className="border border-stone-300 bg-white px-1.5 py-px text-[10px] font-medium text-stone-600" data-testid="hub-assigned-count">
            {assignedCount} firma eşleştirildi
          </span>
        </div>
        <p className="text-[11px] text-stone-500">Firma hub'a eşleştirilince, holü boş olan siparişlerde Hol alanında hub adı gösterilir.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 px-4 py-3">
        <Input
          value={newHub}
          onChange={(e) => setNewHub(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addHub()}
          placeholder="Yeni hub adı (örn: İklim Finansmanı Hub)"
          data-testid="hub-new-input"
          className="w-[280px] rounded-sm border-stone-300"
        />
        <Button onClick={addHub} size="sm" data-testid="hub-add-button" className="h-9 rounded-sm bg-[#1C4E35] text-xs hover:bg-[#163D2A] active:scale-95">
          <Plus size={13} className="mr-1" /> Hub Ekle
        </Button>
        <div className="flex flex-wrap gap-1.5" data-testid="hub-chip-list">
          {hubs.map((h) => (
            <span key={h} className="flex items-center gap-1 border border-[#1C4E35]/40 bg-[#1C4E35]/5 px-2 py-1 text-[11px] font-medium text-[#1C4E35]" data-testid={`hub-chip-${h.replace(/\s+/g, "_")}`}>
              {h}
              <button
                type="button"
                onClick={() => window.confirm(`"${h}" hub'ını silmek istediğinize emin misiniz? Bu hub'a eşleştirilmiş firmalar da kaldırılacak.`) && removeHub(h)}
                data-testid={`hub-delete-${h.replace(/\s+/g, "_")}`}
                className="text-[#1C4E35]/60 transition-colors hover:text-red-600"
              >
                <X size={11} />
              </button>
            </span>
          ))}
          {hubs.length === 0 && <span className="text-[11px] text-stone-400" data-testid="hub-empty">Henüz hub eklenmedi.</span>}
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="relative mb-2 w-[260px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Firma ara..."
            data-testid="hub-org-search"
            className="h-8 rounded-sm border-stone-300 pl-8 text-xs"
          />
        </div>
        <div className="max-h-[420px] overflow-y-auto border border-stone-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-xs">Firma</TableHead>
                <TableHead className="w-[280px] text-right text-xs">Hub</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleOrgs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className="py-5 text-center text-sm text-stone-400" data-testid="hub-org-empty">Firma bulunamadı.</TableCell>
                </TableRow>
              )}
              {visibleOrgs.map((org) => (
                <TableRow key={org} data-testid={`hub-org-row-${org.replace(/\s+/g, "_")}`} className="hover:bg-stone-50">
                  <TableCell className="py-1.5 text-sm text-[#0A0D0B]">
                    {org}
                    {assignments[org] && (
                      <span className="ml-2 border border-[#1C4E35]/40 bg-[#1C4E35]/5 px-1.5 py-px text-[10px] font-medium text-[#1C4E35]" data-testid={`hub-org-badge-${org.replace(/\s+/g, "_")}`}>
                        {assignments[org]}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5 text-right">
                    <Select value={assignments[org] || NONE} onValueChange={(v) => setOrgHub(org, v)}>
                      <SelectTrigger className="ml-auto h-8 w-[260px] rounded-sm border-stone-300 text-xs" data-testid={`hub-org-select-${org.replace(/\s+/g, "_")}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>— Atanmadı —</SelectItem>
                        {hubs.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
